import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { EChartsRenderer } from "@/shared/charts/components/echarts-renderer";
import { createMultiLineChartOption } from "@/shared/charts/components/multi-line-chart/option";
import type {
  MultiLineChartConfig,
  MultiLineChartPin,
  MultiLineChartPoint,
} from "@/shared/charts/components/multi-line-chart/types";
import {
  createEChartsTheme,
  type EChartsThemeOverride,
} from "@/shared/charts/theme/create-echarts-theme";
import type { ECElementEvent, ECharts } from "echarts";

interface TimestampedDataIndex {
  timestamp: number;
  dataIndex: number;
}

interface ActiveTooltipPosition {
  left: number;
  alignment: "left" | "center" | "right";
}

interface ChartPin {
  key: string;
  pin: MultiLineChartPin;
}

interface PositionedChartPin extends ChartPin, ActiveTooltipPosition {
  dataPoint: MultiLineChartPoint;
}

const ACTIVE_TOOLTIP_WIDTH = 220;
const PIN_TOOLTIP_BOTTOM = 2;
const EMPTY_PINS: readonly MultiLineChartPin[] = [];

function createTimestampLookup(data: MultiLineChartPoint[]): TimestampedDataIndex[] {
  const dataIndexesByTimestamp = new Map<number, number>();
  data.forEach((point, dataIndex) => {
    if (point.timestamp == null) return;
    const timestamp = Date.parse(point.timestamp);
    if (!Number.isFinite(timestamp)) return;

    const existingDataIndex = dataIndexesByTimestamp.get(timestamp);
    if (existingDataIndex == null || dataIndex < existingDataIndex) {
      dataIndexesByTimestamp.set(timestamp, dataIndex);
    }
  });

  return [...dataIndexesByTimestamp.entries()]
    .map(([timestamp, dataIndex]) => ({ timestamp, dataIndex }))
    .sort((left, right) => left.timestamp - right.timestamp);
}

function findNearestDataIndex(
  timestampLookup: TimestampedDataIndex[],
  timestamp: number
): number | null {
  if (!Number.isFinite(timestamp) || timestampLookup.length === 0) return null;

  let low = 0;
  let high = timestampLookup.length;
  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    const candidate = timestampLookup.at(middle);
    if (candidate == null || candidate.timestamp < timestamp) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  const right = timestampLookup.at(low);
  const left = timestampLookup.at(low - 1);
  if (left == null) return right?.dataIndex ?? null;
  if (right == null) return left.dataIndex;

  const leftDistance = Math.abs(left.timestamp - timestamp);
  const rightDistance = Math.abs(right.timestamp - timestamp);
  if (leftDistance === rightDistance) return Math.min(left.dataIndex, right.dataIndex);
  return leftDistance < rightDistance ? left.dataIndex : right.dataIndex;
}

interface MultiLineChartProps {
  data: MultiLineChartPoint[];
  config: MultiLineChartConfig;
  height?: number | string;
  /** Partial visual-token overrides merged with the MUI-derived chart theme. */
  theme?: EChartsThemeOverride;
  /** External time cursor used to render a persistent selected-point tooltip. */
  activeTimestamp?: number;
  /** Additional persistent pins, for example a secondary scrubber hover position. */
  additionalPins?: readonly MultiLineChartPin[];
  onChartReady?: (instance: ECharts) => void;
}

export const MultiLineChart = memo(function MultiLineChart({
  data,
  config,
  height = 240,
  theme,
  activeTimestamp,
  additionalPins,
  onChartReady,
}: MultiLineChartProps) {
  const muiTheme = useTheme();
  const seriesIds = useMemo(() => config.series.map((series) => series.id), [config.series]);
  const seriesKey = seriesIds.join("\u0000");
  const [visibleSeriesIds, setVisibleSeriesIds] = useState<ReadonlySet<string>>(
    () => new Set(seriesIds)
  );
  const [positionedPins, setPositionedPins] = useState<PositionedChartPin[]>([]);
  const [chartReadyVersion, setChartReadyVersion] = useState(0);
  const chartTokens = useMemo(() => createEChartsTheme(muiTheme, theme), [muiTheme, theme]);
  const chartRef = useRef<ECharts | null>(null);
  const timestampLookup = useMemo(() => createTimestampLookup(data), [data]);

  useEffect(() => {
    const nextVisibleSeriesIds = new Set(seriesKey.split("\u0000"));
    setVisibleSeriesIds((currentVisibleSeriesIds) => {
      const hasSameSeries =
        currentVisibleSeriesIds.size === nextVisibleSeriesIds.size &&
        [...nextVisibleSeriesIds].every((seriesId) => currentVisibleSeriesIds.has(seriesId));
      return hasSameSeries ? currentVisibleSeriesIds : nextVisibleSeriesIds;
    });
  }, [seriesKey]);

  const option = useMemo(
    () => createMultiLineChartOption(data, { ...config, visibleSeriesIds }, chartTokens),
    [data, config, chartTokens, visibleSeriesIds]
  );
  const chartPins = useMemo<ChartPin[]>(() => {
    const pins: ChartPin[] =
      activeTimestamp == null ? [] : [{ key: "active", pin: { timestamp: activeTimestamp } }];

    for (const [index, pin] of (additionalPins ?? EMPTY_PINS).entries()) {
      pins.push({ key: `additional-${pin.id ?? index}`, pin });
    }

    return pins;
  }, [activeTimestamp, additionalPins]);

  const updateActiveTooltipPosition = useCallback(() => {
    const chart = chartRef.current;
    if (chart == null || chart.isDisposed() || chartPins.length === 0) {
      setPositionedPins([]);
      return;
    }

    const chartWidth = chart.getWidth();
    if (chartWidth <= 0) {
      setPositionedPins([]);
      return;
    }

    const edgePadding = 8;
    const nextPositionedPins = chartPins.flatMap(({ key, pin }) => {
      const dataIndex = findNearestDataIndex(timestampLookup, pin.timestamp);
      const dataPoint = dataIndex == null ? undefined : data.at(dataIndex);
      const dataPointTimestamp =
        dataPoint?.timestamp == null ? null : Date.parse(dataPoint.timestamp);
      if (dataPoint == null || dataPointTimestamp == null || !Number.isFinite(dataPointTimestamp)) {
        return [];
      }

      const x = chart.convertToPixel({ xAxisIndex: 0 }, dataPointTimestamp);
      if (typeof x !== "number" || !Number.isFinite(x)) return [];

      const left = Math.min(Math.max(x, edgePadding), chartWidth - edgePadding);
      const alignment: ActiveTooltipPosition["alignment"] =
        x < ACTIVE_TOOLTIP_WIDTH / 2
          ? "left"
          : x > chartWidth - ACTIVE_TOOLTIP_WIDTH / 2
            ? "right"
            : "center";
      return [{ key, pin, dataPoint, left, alignment }];
    });

    setPositionedPins(nextPositionedPins);
  }, [chartPins, data, timestampLookup]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(updateActiveTooltipPosition);
    const handleResize = () => updateActiveTooltipPosition();
    window.addEventListener("resize", handleResize);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", handleResize);
    };
  }, [chartReadyVersion, option, updateActiveTooltipPosition]);

  const handleChartReady = useCallback(
    (instance: ECharts) => {
      chartRef.current = instance;
      setChartReadyVersion((version) => version + 1);
      onChartReady?.(instance);
    },
    [onChartReady]
  );

  const handleLegendSelectionChanged = useCallback(
    (params: ECElementEvent) => {
      if (
        !("selected" in params) ||
        typeof params.selected !== "object" ||
        params.selected == null
      ) {
        return;
      }

      const selectedEntries = Object.entries(params.selected);
      setVisibleSeriesIds(
        new Set(
          config.series
            .filter(
              (series) => selectedEntries.find(([label]) => label === series.label)?.[1] !== false
            )
            .map((series) => series.id)
        )
      );
    },
    [config.series]
  );

  const activeTooltipSeries = config.series.filter((series) => visibleSeriesIds.has(series.id));

  return (
    <Box sx={{ position: "relative", width: "100%", height, minHeight: 220 }}>
      <EChartsRenderer
        option={option}
        ariaLabel={config.ariaDescription}
        height="100%"
        onChartReady={handleChartReady}
        // Replace dynamic collections so hidden thresholds remove prior visual maps instead of
        // retaining their severity colours through ECharts option merging.
        notMerge={false}
        replaceMerge={["series", "legend", "visualMap"]}
        lazyUpdate={false}
        onEvents={{ legendselectchanged: handleLegendSelectionChanged }}
      />
      {positionedPins.map(({ key, pin, dataPoint, left, alignment }) => {
        const lineColor = pin.color ?? chartTokens.highlightColor;
        const tooltipBackgroundColor =
          pin.tooltipBackgroundColor ?? chartTokens.tooltipBackgroundColor;
        const tooltipTextColor = pin.tooltipTextColor ?? chartTokens.textColor;
        return (
          <Fragment key={key}>
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                left,
                top: 44,
                bottom: 42,
                zIndex: 1,
                width: 0,
                borderLeft: `2px ${pin.lineType ?? "solid"} ${lineColor}`,
                pointerEvents: "none",
                transform: "translateX(-50%)",
              }}
            />
            <Box
              aria-label={`Selected time ${dataPoint.x}`}
              sx={{
                position: "absolute",
                left,
                bottom: PIN_TOOLTIP_BOTTOM,
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
                minWidth: 150,
                maxWidth: `min(${ACTIVE_TOOLTIP_WIDTH}px, calc(100% - 16px))`,
                border: `1px solid ${chartTokens.tooltipBorderColor}`,
                borderRadius: 1,
                bgcolor: tooltipBackgroundColor,
                boxShadow: 3,
                px: 1,
                py: 0.75,
                pointerEvents: "none",
                transform:
                  alignment === "left"
                    ? "none"
                    : alignment === "right"
                      ? "translateX(-100%)"
                      : "translateX(-50%)",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: tooltipTextColor,
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {dataPoint.x}
              </Typography>
              {activeTooltipSeries.map((series, index) => {
                const value = dataPoint.values[series.id];
                const formattedValue =
                  value == null
                    ? "Unavailable"
                    : (config.valueFormatter?.(value) ?? value.toLocaleString("en-GB"));
                const color =
                  series.color ??
                  chartTokens.categoricalPalette.at(
                    index % chartTokens.categoricalPalette.length
                  ) ??
                  chartTokens.primarySeriesColor;
                return (
                  <Box key={series.id} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box
                      aria-hidden
                      sx={{
                        width: 6,
                        height: 6,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: color,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: tooltipTextColor, lineHeight: 1.4, overflowWrap: "anywhere" }}
                    >
                      {series.label}: {formattedValue}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Fragment>
        );
      })}
    </Box>
  );
});
